import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, Component } from '@angular/core';

import { Coord } from '@everyboard/games';
import { PointyHexaOrientation } from '@everyboard/games';
import { DvonnMaxStacksHeuristic } from '@everyboard/games/dvonn';
import { DvonnMove } from '@everyboard/games/dvonn';
import { DvonnMoveGenerator } from '@everyboard/games/dvonn';
import { DvonnPieceStack } from '@everyboard/games/dvonn';
import { DvonnRules } from '@everyboard/games/dvonn';
import { DvonnScoreHeuristic } from '@everyboard/games/dvonn';
import { DvonnState } from '@everyboard/games/dvonn';
import { MGPFallible, MGPOptional, MGPValidation, Utils } from '@everyboard/lib';

import { ViewBox } from '../../components/game-components/GameComponentUtils';
import { HexaLayout } from '../../components/game-components/HexaLayout';
import { ClickHandler } from '../../components/game-components/game-component/ClickHandler';
import { HexagonalGameComponent } from '../../components/game-components/game-component/HexagonalGameComponent';

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: 'app-dvonn',
    templateUrl: './dvonn.component.html',
    styleUrls: ['../../components/game-components/game-component/game-component.scss'],
    imports: [NgClass],
})

export class DvonnComponent extends HexagonalGameComponent<DvonnRules, DvonnMove, DvonnState, DvonnPieceStack> {

    protected override computeViewBox(): ViewBox {
        return new ViewBox(0, 0, 875, 380);
    }

    public lastMove: MGPOptional<DvonnMove> = MGPOptional.empty();
    public chosen: MGPOptional<Coord> = MGPOptional.empty();
    public disconnectedSpaces: { coord: Coord; spaceContent: DvonnPieceStack }[] = [];

    public constructor() {
        super('Dvonn');
        this.aiConfig = {
            minimax: [
                {
                    id: 'Stacks',
                    name: $localize`Stacks`,
                    heuristic: (): DvonnMaxStacksHeuristic => new DvonnMaxStacksHeuristic(),
                    moveGenerator: (): DvonnMoveGenerator => new DvonnMoveGenerator(),
                },
                {
                    id: 'Score',
                    name: $localize`Score`,
                    heuristic: (): DvonnScoreHeuristic => new DvonnScoreHeuristic(),
                    moveGenerator: (): DvonnMoveGenerator => new DvonnMoveGenerator(),
                },
            ],
            mcts: [
                {
                    id: 'default',
                    name: $localize`Default`,
                    moveGenerator: (): DvonnMoveGenerator => new DvonnMoveGenerator(),
                },
                {
                    id: 'Score',
                    name: $localize`Score`,
                    heuristic: (): DvonnScoreHeuristic => new DvonnScoreHeuristic(),
                    moveGenerator: (): DvonnMoveGenerator => new DvonnMoveGenerator(),
                },
            ],
        };
        this.encoder = DvonnMove.encoder;
        this.scores = MGPOptional.of(DvonnRules.getScores(this.state()));

        this.SPACE_SIZE = 30;
        this.hexaLayout = new HexaLayout(this.SPACE_SIZE * 1.50,
                                         new Coord(-this.SPACE_SIZE, this.SPACE_SIZE * 2),
                                         PointyHexaOrientation.INSTANCE);
        this.hexaBoard = this.state().board;
    }

    public override hideLastMove(): void {
        this.lastMove = MGPOptional.empty();
        this.disconnectedSpaces = [];
    }

    public override async updateBoard(_triggerAnimation: boolean): Promise<void> {
        this.canPass = this.rules.canOnlyPass(this.state());
        this.scores = MGPOptional.of(DvonnRules.getScores(this.state()));
    }

    protected override async showLastMove(move: DvonnMove): Promise<void> {
        this.lastMove = MGPOptional.of(move);
        const previousState: DvonnState = this.getPreviousState();
        const state: DvonnState = this.state();
        for (let y: number = 0; y < state.getHeight(); y++) {
            for (let x: number = 0; x < state.board[y].length; x++) {
                const coord: Coord = new Coord(x, y);
                if (state.isOnBoard(coord) &&
                    coord.equals(this.lastMove.get().getStart()) === false)
                {
                    const stack: DvonnPieceStack = state.getPieceAt(coord);
                    const previousStack: DvonnPieceStack = previousState.getPieceAt(coord);
                    if (stack.isEmpty() && previousStack.hasPieces()) {
                        const disconnected: { coord: Coord; spaceContent: DvonnPieceStack } =
                            { coord, spaceContent: previousStack };
                        this.disconnectedSpaces.push(disconnected);
                    }
                }
            }
        }
    }

    public override cancelMoveAttempt(): void {
        this.chosen = MGPOptional.empty();
    }

    public override async pass(): Promise<MGPValidation> {
        Utils.assert(this.canPass, 'DvonnComponent: pass() can only be called if canPass is true');
        return await this.chooseMove(DvonnMove.PASS);
    }

    @ClickHandler((x: number, y: number) => `#click-${ x }-${ y }`)
    public async onClick(x: number, y: number): Promise<MGPValidation> {
        if (this.chosen.isAbsent()) {
            return this.choosePiece(x, y);
        } else if (this.chosen.equalsValue(new Coord(x, y))) {
            // Deselects the piece
            return this.cancelMove();
        } else {
            return await this.chooseDestination(x, y);
        }
    }

    public async choosePiece(x: number, y: number): Promise<MGPValidation> {
        const coord: Coord = new Coord(x, y);
        const legal: MGPValidation = this.rules.isMovablePiece(this.state(), coord);
        if (legal.isSuccess()) {
            this.chosen = MGPOptional.of(coord);
            return MGPValidation.SUCCESS;
        } else {
            return this.cancelMove(legal.getReason());
        }
    }

    private async chooseDestination(x: number, y: number): Promise<MGPValidation> {
        const state: DvonnState = this.state();
        const chosenPiece: Coord = this.chosen.get();
        const chosenDestination: Coord = new Coord(x, y);
        const move: MGPFallible<DvonnMove> = DvonnMove.from(chosenPiece, chosenDestination);
        if (move.isFailure()) {
            if (this.rules.isMovablePiece(state, chosenDestination).isSuccess()) {
                // Change piece selection
                return this.choosePiece(x, y);
            } else {
                return this.cancelMove(move.getReason());
            }
        } else {
            // The only way to know whether we can select the other piece is to check the move legality
            const legality: MGPValidation = this.rules.isLegal(move.get(), state, this.config());
            if (legality.isFailure() && this.rules.isMovablePiece(state, chosenDestination).isSuccess()) {
                return this.choosePiece(x, y);
            } else {
                return this.chooseMove(move.get());
            }
        }
    }

    public getPieceClasses(stack: DvonnPieceStack): string[] {
        if (stack.containsSource() && stack.getSize() === 1) {
            return ['nonplayer-fill', 'dashed-stroke'];
        }
        const playerColor: string = this.getPlayerClass(stack.getOwner());
        if (stack.containsSource()) {
            return [playerColor, 'nonplayer-stroke', 'dashed-stroke'];
        }
        return [playerColor];
    }

    public getTextSizeClass(spaceContent: DvonnPieceStack): string {
        if (spaceContent.containsSource()) {
            if (spaceContent.size <= 9) {
                return 'text-medium';
            } else {
                return 'text-small-plus';
            }
        } else {
            return 'text-medium-plus';
        }
    }

    public getSourceSymbolTransform(spaceContent: DvonnPieceStack): string {
        if (spaceContent.size === 1) { // 1 -> no number written
            return 'translate(-18, -16) scale(0.10)';
        } else if (spaceContent.size <= 9) { // 2 to 9 written
            return 'translate(-3, -12) scale(0.07)';
        } else { // [10, 49] written
            return 'translate(3, -12) scale(0.06)';
        }
    }

    public getTextTransform(spaceContent: DvonnPieceStack): string {
        const containsSource: boolean = spaceContent.containsSource();
        if (spaceContent.size <= 9) {
            if (containsSource) { // X Z or Z
                return 'translate(-7, 0)';
            } else { // X or ""
                return 'translate(0, 0)';
            }
        } else {
            if (containsSource) { // X X Z
                return 'translate(-9, -3)';
            } else { // X X
                return 'translate(0, 0)';
            }
        }
    }

}
